import React, { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { DependentSelect } from '../ui/dependent-select';
import { ReviewPeriodSelect } from '../ui/review-period-select';
import { X, Upload, FileText, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useMultipleCodelists } from '../../hooks/useCodelist';
import { useNis2Selection } from '../../hooks/useNis2Selection';
import { api } from '../../lib/api';

interface CsvImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

interface CsvPreviewData {
  filename: string;
  originalName: string;
  headers: string[];
  previewData: Record<string, string>[];
  totalColumns: number;
  totalRows: number;
  detectedDelimiter: string;
}

interface ImportResult {
  processedRows: number;
  createdAssets: number;
  updatedAssets: number;
  detectedDelimiter?: string;
  errors: Array<{
    row: number;
    error: string;
    data: Record<string, string>;
  }>;
}

const CsvImportDialog: React.FC<CsvImportDialogProps> = ({
  isOpen,
  onClose,
  onImportComplete
}) => {
  const [step, setStep] = useState<'upload' | 'configure' | 'importing' | 'complete'>('upload');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<CsvPreviewData | null>(null);
  const [uploading, setUploading] = useState(false);
  const [, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  
  // Configuration state
  const [selectedNameColumn, setSelectedNameColumn] = useState('');
  const [selectedDescriptionColumn, setSelectedDescriptionColumn] = useState('');
  const [commonAttributes, setCommonAttributes] = useState({
    asset_category: '',
    type: '',
    classification: 'primary' as 'primary' | 'supporting',
    nis2_chapter: '', // Keep for backward compatibility
    nis2_article: '',
    nis2_letter: '',
    owner_id: undefined as number | undefined,
    custodian_id: undefined as number | undefined,
    location: '',
    confidentiality_level: 'internal' as 'public' | 'internal' | 'confidential' | 'restricted',
    integrity_level: 'medium' as 'low' | 'medium' | 'high',
    availability_level: 'medium' as 'low' | 'medium' | 'high',
    value_assessment: 'medium' as 'low' | 'medium' | 'high' | 'critical',
    review_period: '1y',
    status: 'active' as 'active' | 'inactive' | 'retired'
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  
  // Load codelists for dropdowns
  useMultipleCodelists([
    'nis2_chapters', // Keep for backward compatibility
    'nis2_articles', 
    'nis2_letters',
    'asset_types_nis2', 
    'asset_categories'
  ]);

  // Handle NIS2 selection with proper auto-selection behavior
  const nis2Selection = useNis2Selection({
    initialArticle: commonAttributes.nis2_article || '',
    initialLetter: commonAttributes.nis2_letter || '',
    onSelectionChange: (selection) => {
      setCommonAttributes(prev => ({
        ...prev,
        nis2_article: selection.nis2_article,
        nis2_letter: selection.nis2_letter
      }));
    }
  });

  // Reset dialog state
  const resetDialog = () => {
    console.log('=== resetDialog called ===');
    console.trace('resetDialog call stack');
    setStep('upload');
    setCsvFile(null);
    setCsvPreview(null);
    setSelectedNameColumn('');
    setSelectedDescriptionColumn('');
    setImportResult(null);
    setCommonAttributes({
      asset_category: '',
      type: '',
      classification: 'primary',
      nis2_chapter: '', // Keep for backward compatibility
      nis2_article: '',
      nis2_letter: '',
      owner_id: undefined,
      custodian_id: undefined,
      location: '',
      confidentiality_level: 'internal',
      integrity_level: 'medium',
      availability_level: 'medium',
      value_assessment: 'medium',
      review_period: '1y',
      status: 'active'
    });
    
    // Reset NIS2 selection hook
    nis2Selection.setSelection({
      nis2_article: '',
      nis2_letter: ''
    });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
        setCsvFile(file);
      } else {
        toast({
          title: "Chyba",
          description: "Vyberte prosím CSV soubor",
          variant: "destructive",
        });
      }
    }
  };

  const handleUpload = async () => {
    if (!csvFile) {
      toast({
        title: "Chyba",
        description: "Vyberte prosím CSV soubor",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('csvFile', csvFile);

      const response = await api.postFormData<{ success: boolean; data: CsvPreviewData }>('/assets/csv/preview', formData);
      
      if (response.success) {
        setCsvPreview(response.data);
        console.log('Setting step to: configure (after successful upload)');
        setStep('configure');
        toast({
          title: "Úspěch",
          description: "CSV soubor byl úspěšně nahrán a zpracován",
        });
      }
    } catch (error: any) {
      console.error('CSV upload error:', error);
      toast({
        title: "Chyba",
        description: error.response?.data?.message || "Nepodařilo se nahrát CSV soubor",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleImport = async () => {
    console.log('=== handleImport started ===');
    
    if (!csvPreview || !selectedNameColumn) {
      console.log('Validation failed: missing csvPreview or selectedNameColumn');
      toast({
        title: "Chyba",
        description: "Vyberte prosím sloupec pro název assetu",
        variant: "destructive",
      });
      return;
    }

    // Validate required common attributes
    if (!commonAttributes.asset_category || !commonAttributes.type || !commonAttributes.classification) {
      console.log('Validation failed: missing required common attributes');
      toast({
        title: "Chyba", 
        description: "Vyplňte prosím povinné společné atributy (kategorie, typ, klasifikace)",
        variant: "destructive",
      });
      return;
    }

    console.log('Validation passed, starting import...');
    setImporting(true);
    console.log('Setting step to: importing');
    setStep('importing');
    
    try {
      const importData = {
        filename: csvPreview.filename,
        nameColumn: selectedNameColumn,
        descriptionColumn: selectedDescriptionColumn || undefined,
        commonAttributes: commonAttributes
      };

      const response = await api.post<{ success: boolean; data: ImportResult; message: string }>('/assets/csv/import', importData);
      
      console.log('CSV import response:', response);
      console.log('Import result data:', response.data);
      console.log('Errors in result:', response.data?.errors);
      console.log('Setting importResult to:', response.data);
      console.log('Setting step to: complete');
      
      if (response.success) {
        setImportResult(response.data);
        setStep('complete');
        console.log('Step set to complete, importResult set');
        toast({
          title: "Import dokončen",
          description: response.message,
        });
        // Zavolej onImportComplete ale NEZAVÍREJ dialog automaticky
        // Uživatel ho zavře ručně tlačítkem "Zavřít"
        console.log('About to call onImportComplete callback');
        onImportComplete();
        console.log('onImportComplete callback finished');
        // ODSTRANIT: onClose(); - necháme dialog otevřený
      } else {
        console.log('Response was not successful:', response);
      }
    } catch (error: any) {
      console.error('CSV import error:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      console.log('Setting step back to configure due to error');
      toast({
        title: "Chyba importu",
        description: error.response?.data?.message || "Nepodařilo se importovat CSV soubor",
        variant: "destructive",
      });
      console.log('Setting step to: configure (due to error)');
      setStep('configure');
    } finally {
      setImporting(false);
      console.log('Import process finished, importing set to false');
    }
  };

  const handleClose = () => {
    console.log('=== handleClose called ===');
    console.trace('handleClose call stack');
    resetDialog();
    onClose();
  };

  if (!isOpen) return null;

  // Debug render state
  console.log('=== RENDERING CsvImportDialog ===');
  console.log('Current step:', step);
  console.log('ImportResult exists:', !!importResult);
  console.log('ImportResult errors:', importResult?.errors?.length || 0);
  console.log('Render condition (step === complete && importResult):', step === 'complete' && !!importResult);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">Import Assets z CSV souboru</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-6 w-6 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-6">
          {step === 'upload' && (
            <>
              {(() => { console.log('RENDERING: upload step'); return null; })()}
              <div className="space-y-6">
              <div className="text-center">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-lg font-medium">Vyberte CSV soubor</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Nahrajte CSV soubor s daty pro import assets
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="csvFile">CSV soubor</Label>
                  <input
                    ref={fileInputRef}
                    id="csvFile"
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="mt-1 block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-md file:border-0
                      file:text-sm file:font-medium
                      file:bg-blue-50 file:text-blue-700
                      hover:file:bg-blue-100"
                  />
                </div>

                {csvFile && (
                  <div className="p-3 bg-gray-50 rounded-md">
                    <p className="text-sm">
                      <strong>Vybraný soubor:</strong> {csvFile.name}
                    </p>
                    <p className="text-sm text-gray-500">
                      Velikost: {(csvFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button onClick={handleUpload} disabled={!csvFile || uploading}>
                    {uploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Nahrávám...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Nahrát a zobrazit náhled
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
            </>
          )}

          {step === 'configure' && csvPreview && (
            <>
              {(() => { 
                console.log('RENDERING: configure step'); 
                console.log('selectedNameColumn in render:', selectedNameColumn);
                console.log('commonAttributes.asset_category:', commonAttributes.asset_category);
                console.log('commonAttributes.type:', commonAttributes.type);
                return null; 
              })()}
              <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-2">Konfigurace importu</h3>
                <div className="text-sm text-gray-600 mb-4 flex flex-wrap gap-6">
                  <span>Soubor: <strong>{csvPreview.originalName}</strong></span>
                  <span>Sloupce: <strong>{csvPreview.totalColumns}</strong></span>
                  <span>Řádky: <strong>{csvPreview.totalRows}</strong></span>
                  <span>Detekovaný oddělovač: <strong>{csvPreview.detectedDelimiter}</strong></span>
                </div>
              </div>

              {/* Column mapping */}
              <Card>
                <CardHeader>
                  <CardTitle>Mapování sloupců</CardTitle>
                  <CardDescription>
                    Vyberte které sloupce odpovídají názvu a popisu assetu
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nameColumn">Název assetu *</Label>
                      <select
                        id="nameColumn"
                        value={selectedNameColumn}
                        onChange={(e) => setSelectedNameColumn(e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">-- Vyberte sloupec --</option>
                        {csvPreview.headers.map((header) => (
                          <option key={header} value={header}>{header}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="descriptionColumn">Popis assetu (volitelné)</Label>
                      <select
                        id="descriptionColumn"
                        value={selectedDescriptionColumn}
                        onChange={(e) => setSelectedDescriptionColumn(e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">-- Žádný sloupec --</option>
                        {csvPreview.headers.map((header) => (
                          <option key={header} value={header}>{header}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Preview data - moved up */}
              <Card>
                <CardHeader>
                  <CardTitle>Náhled dat</CardTitle>
                  <CardDescription>
                    Prvních 5 řádků z CSV souboru
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {csvPreview.headers.map((header) => (
                            <th
                              key={header}
                              className={`px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                                header === selectedNameColumn ? 'bg-blue-100' : 
                                header === selectedDescriptionColumn ? 'bg-green-100' : ''
                              }`}
                            >
                              {header}
                              {header === selectedNameColumn && (
                                <Badge className="ml-1 bg-blue-500">Název</Badge>
                              )}
                              {header === selectedDescriptionColumn && (
                                <Badge className="ml-1 bg-green-500">Popis</Badge>
                              )}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {csvPreview.previewData.map((row, index) => (
                          <tr key={index}>
                            {csvPreview.headers.map((header) => (
                              <td
                                key={header}
                                className={`px-3 py-2 whitespace-nowrap text-sm text-gray-900 ${
                                  header === selectedNameColumn ? 'bg-blue-50' : 
                                  header === selectedDescriptionColumn ? 'bg-green-50' : ''
                                }`}
                              >
                                {row[header] || '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Common attributes */}
              <Card>
                <CardHeader>
                  <CardTitle>Společné atributy</CardTitle>
                  <CardDescription>
                    Tyto hodnoty budou aplikovány na všechny importované assets
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <DependentSelect
                        label="Typ assetu"
                        parentValue={commonAttributes.asset_category}
                        childValue={commonAttributes.type}
                        onParentChange={(value) => setCommonAttributes(prev => ({ ...prev, asset_category: Array.isArray(value) ? value[0] || '' : value }))}
                        onChildChange={(value) => setCommonAttributes(prev => ({ ...prev, type: Array.isArray(value) ? value[0] || '' : value }))}
                        parentCodelistType="asset_categories"
                        childCodelistType="asset_types_nis2"
                        parentLabel="Kategorie"
                        childLabel="Typ"
                        required={true}
                        disabled={false}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        ⚠️ Select a kategorie to enable adding new typs
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="classification">Klasifikace *</Label>
                        <select
                          id="classification"
                          value={commonAttributes.classification}
                          onChange={(e) => setCommonAttributes(prev => ({ 
                            ...prev, 
                            classification: e.target.value as 'primary' | 'supporting' 
                          }))}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="primary">Primární</option>
                          <option value="supporting">Podpůrný</option>
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="location">Umístění</Label>
                        <Input
                          id="location"
                          value={commonAttributes.location}
                          onChange={(e) => setCommonAttributes(prev => ({ ...prev, location: e.target.value }))}
                          placeholder="Umístění assetu"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <DependentSelect
                        label="NIS2 Article & Letter"
                        parentValue={nis2Selection.article}
                        childValue={nis2Selection.letter}
                        onParentChange={nis2Selection.handleParentChange}
                        onChildChange={nis2Selection.handleChildChange}
                        parentCodelistType="nis2_articles"
                        childCodelistType="nis2_letters"
                        placeholder="Select NIS2 Article & Letter..."
                        preserveChildOnAutoSelect={true}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="confidentiality_level">Důvernost</Label>
                      <select
                        id="confidentiality_level"
                        value={commonAttributes.confidentiality_level}
                        onChange={(e) => setCommonAttributes(prev => ({ 
                          ...prev, 
                          confidentiality_level: e.target.value as any 
                        }))}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="public">Veřejné</option>
                        <option value="internal">Interní</option>
                        <option value="confidential">Důverné</option>
                        <option value="restricted">Přísně tajné</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="integrity_level">Integrita</Label>
                      <select
                        id="integrity_level"
                        value={commonAttributes.integrity_level}
                        onChange={(e) => setCommonAttributes(prev => ({ 
                          ...prev, 
                          integrity_level: e.target.value as any 
                        }))}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="low">Nízká</option>
                        <option value="medium">Střední</option>
                        <option value="high">Vysoká</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="availability_level">Dostupnost</Label>
                      <select
                        id="availability_level"
                        value={commonAttributes.availability_level}
                        onChange={(e) => setCommonAttributes(prev => ({ 
                          ...prev, 
                          availability_level: e.target.value as any 
                        }))}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="low">Nízká</option>
                        <option value="medium">Střední</option>
                        <option value="high">Vysoká</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="value_assessment">Hodnota</Label>
                      <select
                        id="value_assessment"
                        value={commonAttributes.value_assessment}
                        onChange={(e) => setCommonAttributes(prev => ({ 
                          ...prev, 
                          value_assessment: e.target.value as any 
                        }))}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="low">Nízká</option>
                        <option value="medium">Střední</option>
                        <option value="high">Vysoká</option>
                        <option value="critical">Kritická</option>
                      </select>
                    </div>
                    <div>
                      <ReviewPeriodSelect
                        value={commonAttributes.review_period || '1y'}
                        onChange={(value) => setCommonAttributes(prev => ({ ...prev, review_period: value }))}
                        label="Review Period"
                        showPreview={true}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-2">
                <Button onClick={() => setStep('upload')} variant="outline">
                  Zpět
                </Button>
                <Button 
                  onClick={() => {
                    console.log('=== Import button clicked ===');
                    console.log('selectedNameColumn:', selectedNameColumn);
                    console.log('Button disabled:', !selectedNameColumn);
                    handleImport();
                  }} 
                  disabled={!selectedNameColumn}
                >
                  Spustit import
                </Button>
              </div>
            </div>
            </>
          )}

          {step === 'importing' && (
            <>
              {(() => { console.log('RENDERING: importing step'); return null; })()}
            <div className="text-center py-8">
              <Loader2 className="mx-auto h-12 w-12 animate-spin text-blue-500" />
              <h3 className="mt-4 text-lg font-medium">Probíhá import...</h3>
              <p className="mt-2 text-sm text-gray-500">
                Zpracováváme CSV soubor a vytváříme/aktualizujeme assets
              </p>
            </div>
            </>
          )}

          {step === 'complete' && importResult && (
            <>
              {(() => { console.log('RENDERING: complete step with importResult'); return null; })()}
            <div className="space-y-6">
              <div className="text-center">
                <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
                <h3 className="mt-2 text-lg font-medium">Import dokončen</h3>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {importResult.processedRows}
                    </div>
                    <div className="text-sm text-gray-600">Zpracováno řádků</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {importResult.createdAssets}
                    </div>
                    <div className="text-sm text-gray-600">Vytvořeno assets</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {importResult.updatedAssets}
                    </div>
                    <div className="text-sm text-gray-600">Aktualizováno assets</div>
                  </CardContent>
                </Card>
              </div>

              {importResult.errors && importResult.errors.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <AlertCircle className="mr-2 h-5 w-5 text-red-500" />
                      Chyby při importu ({importResult.errors.length})
                    </CardTitle>
                    <CardDescription>
                      Následující řádky nebyly importovány kvůli chybám
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Summary of errors */}
                    <div className="mb-4 space-y-2">
                      {importResult.errors.map((error, index) => (
                        <div key={index} className="p-2 bg-red-50 rounded border-l-4 border-red-400">
                          <div className="text-sm font-medium text-red-800">
                            Řádek {error.row}: {error.error}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Table preview of error rows */}
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Náhled chybných řádků:</h4>
                      <div className="overflow-x-auto border rounded-md">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Řádek
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Chyba
                              </th>
                              {/* Show headers from first error data */}
                              {importResult.errors[0]?.data && Object.keys(importResult.errors[0].data).slice(0, 6).map((header) => (
                                <th
                                  key={header}
                                  className={`px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                                    header === selectedNameColumn ? 'bg-blue-100' : 
                                    header === selectedDescriptionColumn ? 'bg-green-100' : ''
                                  }`}
                                >
                                  {header}
                                  {header === selectedNameColumn && (
                                    <Badge className="ml-1 bg-blue-500 text-xs">Název</Badge>
                                  )}
                                  {header === selectedDescriptionColumn && (
                                    <Badge className="ml-1 bg-green-500 text-xs">Popis</Badge>
                                  )}
                                </th>
                              ))}
                              {importResult.errors[0]?.data && Object.keys(importResult.errors[0].data).length > 6 && (
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  ... ({Object.keys(importResult.errors[0].data).length - 6} dalších)
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {importResult.errors.map((error, index) => (
                              <tr key={index} className="bg-red-50/30">
                                <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-red-700">
                                  {error.row}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-red-600 max-w-xs truncate">
                                  {error.error}
                                </td>
                                {error.data && Object.entries(error.data).slice(0, 6).map(([key, value]) => (
                                  <td
                                    key={key}
                                    className={`px-3 py-2 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate ${
                                      key === selectedNameColumn ? 'bg-blue-50 font-medium' : 
                                      key === selectedDescriptionColumn ? 'bg-green-50' : ''
                                    }`}
                                    title={String(value || '')}
                                  >
                                    {value || '-'}
                                  </td>
                                ))}
                                {error.data && Object.keys(error.data).length > 6 && (
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                    ...
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Tip: Zkontrolujte, zda vybraný sloupec pro název obsahuje data ve všech řádcích.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}


              <div className="flex gap-2">
                <Button onClick={handleClose} className="flex-1">
                  Zavřít
                </Button>
              </div>
            </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CsvImportDialog;
